import React, { useState } from "react";
import { LayoutGroup, motion } from "framer-motion";
import { KeyboardIcon, MousePointerClickIcon } from "lucide-react";
import type { AnswerState, FillGapExercise } from "../../types/exercises";
import { normalize } from "../../utils/text";
import { spring } from "../../utils/motion";
interface FillGapProps {
  exercise: FillGapExercise;
  onAnswer: (a: AnswerState) => void;
  checked: boolean;
}
const chipBase = "focus-ring rounded-xl border-3 border-line px-4 py-2 text-[17px] font-semibold shadow-brutal-sm";
export function FillGap({
  exercise,
  onAnswer,
  checked
}: FillGapProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");
  const solution = `${exercise.before} ${exercise.answer} ${exercise.after}`;
  const report = (value: string) => onAnswer({
    ready: value.trim().length > 0,
    correct: normalize(value) === normalize(exercise.answer),
    solution
  });
  const value = typing ? typed : picked ?? "";
  const isCorrect = normalize(value) === normalize(exercise.answer);
  const gapTone = checked ? isCorrect ? "bg-good-tint" : "bg-bad-tint" : "bg-surface";
  return <LayoutGroup data-id="element-300" data-mp="1sykyl43h17b2">
      <div className="rounded-card border-3 border-line bg-surface p-5 md:p-7" data-id="element-301" data-mp="1sz457ono899s">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-3 text-2xl font-semibold leading-snug text-ink md:text-3xl" lang="es" data-id="element-302" data-mp="1o1m60pzgh8ez">
          <span data-id="element-303" data-mp="108vwenf0z17v">{exercise.before}</span>
          {typing ? <input aria-label="Your answer" value={typed} disabled={checked} autoFocus onChange={e => {
          setTyped(e.target.value);
          report(e.target.value);
        }} className={`w-28 rounded-xl border-3 border-line px-3 py-1 text-center text-xl outline-none focus:shadow-brutal-sm ${gapTone}`} data-id="element-304" data-mp="waemjw47zcmr" /> : picked ? <motion.button layoutId={`gap-${picked}`} type="button" transition={spring.smooth} disabled={checked} onClick={() => {
          setPicked(null);
          report("");
        }} className={`${chipBase} ${gapTone} text-ink`} data-id="element-305" data-mp="2cnnbi6nefpjm">
              {picked}
            </motion.button> : <span className="inline-block h-11 w-24 rounded-xl border-3 border-dashed border-line/50" aria-label="Empty gap" data-id="element-306" data-mp="1tpb00e90m3kv" />}
          <span data-id="element-307" data-mp="1yoe6820qefuf">{exercise.after}</span>
        </p>
        <p className="mt-4 text-[15px] text-ink-2" data-id="element-308" data-mp="23n79z2q7ushe">{exercise.translation}</p>
      </div>

      {!typing && <ul className="mt-6 flex flex-wrap justify-center gap-3" aria-label="Word choices" data-id="element-309" data-mp="27p2ai50bp1u4">
          {exercise.options.map(o => <li key={o} data-id="element-310" data-mp="3f1ksrt644hqk">
              {picked === o ? <span className="block rounded-xl border-3 border-dashed border-line/30 px-4 py-2 text-[17px] font-semibold text-transparent" data-id="element-311" data-mp="3j36d1zkz7yas">
                  {o}
                </span> : <motion.button layoutId={`gap-${o}`} type="button" disabled={checked} whileTap={{
          scale: 0.95
        }} transition={spring.smooth} onClick={() => {
          setPicked(o);
          report(o);
        }} className={`${chipBase} bg-surface text-ink`} lang="es" data-id="element-312" data-mp="13eio0faecsub">
                  {o}
                </motion.button>}
            </li>)}
        </ul>}

      {!checked && <div className="mt-6 flex justify-center" data-id="element-313" data-mp="aqva1j3mt0gn">
          <button type="button" onClick={() => {
        setTyping(t => !t);
        setPicked(null);
        setTyped("");
        report("");
      }} className="focus-ring flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-semibold text-ink-2 hover:text-ink" data-id="element-314" data-mp="as6ell4wic8t">
            {typing ? <MousePointerClickIcon className="h-4 w-4" data-id="element-315" data-mp="16r8oloweo5ax" /> : <KeyboardIcon className="h-4 w-4" data-id="element-316" data-mp="16pxnjwuchbni" />}
            {typing ? "Pick from words instead" : "Type it instead"}
          </button>
        </div>}
    </LayoutGroup>;
}